import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SERVICE_LABEL = {
  rieducazione_motoria: "Riedu",
  small_group: "Small",
  valutazione_iniziale: "Valut",
  coaching_online: "Online",
};
const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  const shift = (first.getDay() + 6) % 7; // Mon=0
  start.setDate(first.getDate() - shift);
  const days = [];
  const cursor = new Date(start);
  for (let i = 0; i < 42; i++) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
    if (i >= 27 && cursor.getMonth() !== month && cursor.getDay() === 1) break;
  }
  return days;
}

export default function MonthCalendar({ role, onDayClick, onSlotClick, refreshKey = 0, initialYearMonth, onMonthChange }) {
  const today = new Date();
  const init = initialYearMonth ? new Date(initialYearMonth) : today;
  const [year, setYear] = useState(init.getFullYear());
  const [month, setMonth] = useState(init.getMonth()); // 0-based
  const [slots, setSlots] = useState([]);

  const days = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const from = fmtDate(days[0]);
  const to = fmtDate(days[days.length - 1]);

  useEffect(() => {
    onMonthChange && onMonthChange(`${year}-${String(month + 1).padStart(2, "0")}-01`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/slots?from_date=${from}&to_date=${to}`);
        setSlots(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [from, to, refreshKey]);

  const slotsByDate = useMemo(() => {
    const m = {};
    slots.forEach(s => {
      m[s.date] = m[s.date] || [];
      m[s.date].push(s);
    });
    Object.values(m).forEach(arr => arr.sort((a, b) => a.time.localeCompare(b.time)));
    return m;
  }, [slots]);

  const prev = () => { const m = month - 1; if (m < 0) { setMonth(11); setYear(year - 1); } else setMonth(m); };
  const next = () => { const m = month + 1; if (m > 11) { setMonth(0); setYear(year + 1); } else setMonth(m); };
  const goToday = () => { setMonth(today.getMonth()); setYear(today.getFullYear()); };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button data-testid="month-prev" onClick={prev} className="w-9 h-9 border border-white/10 hover:border-white/40 flex items-center justify-center">
          <ChevronLeft size={16} />
        </button>
        <button data-testid="month-today" onClick={goToday} className="px-3 h-9 border border-white/10 hover:border-white/40 font-heading text-xs uppercase tracking-widest">Oggi</button>
        <button data-testid="month-next" onClick={next} className="w-9 h-9 border border-white/10 hover:border-white/40 flex items-center justify-center">
          <ChevronRight size={16} />
        </button>
        <div className="ml-3 font-heading uppercase tracking-tight text-lg">
          {MONTHS[month]} {year}
        </div>
        <div className="ml-auto label-caps text-white/40 text-xs">
          Clicca un giorno o uno slot per aprirlo nella vista settimana
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-3">
        {Object.entries(SERVICE_LABEL).map(([k, l]) => (
          <div key={k} className="flex items-center gap-2 text-[11px]">
            <span className={`inline-block w-3 h-3 svc-${k}`} />
            <span className="text-white/60 uppercase tracking-wider">{l}</span>
          </div>
        ))}
      </div>

      {/* Grid header */}
      <div className="card-surface">
        <div className="grid grid-cols-7 border-b border-white/10">
          {WEEKDAYS.map(d => (
            <div key={d} className="p-3 label-caps text-white/60 border-l border-white/10 first:border-l-0">{d}</div>
          ))}
        </div>
        {/* Days */}
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const dateStr = fmtDate(d);
            const inMonth = d.getMonth() === month;
            const isToday = fmtDate(d) === fmtDate(today);
            const dSlots = slotsByDate[dateStr] || [];
            return (
              <div
                key={i}
                data-testid={`month-cell-${dateStr}`}
                onClick={() => onDayClick && onDayClick(dateStr)}
                className={`border-l border-t border-white/5 first:border-l-0 min-h-[120px] p-2 flex flex-col gap-1 cursor-pointer transition-colors hover:bg-white/5 ${inMonth ? "" : "opacity-40"}`}
              >
                <div className={`flex items-center justify-between ${isToday ? "text-[#A855F7]" : ""}`}>
                  <span className="font-heading font-bold text-base">{d.getDate()}</span>
                  {dSlots.length > 0 && (
                    <span className="text-[10px] text-white/40">{dSlots.length}</span>
                  )}
                </div>
                <div className="flex flex-col gap-0.5 overflow-hidden">
                  {dSlots.slice(0, 4).map(s => (
                    <div
                      key={s.id}
                      onClick={(e) => { e.stopPropagation(); onSlotClick && onSlotClick(s); }}
                      className={`svc-${s.service_type} px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider truncate hover:opacity-90`}
                      title={`${s.time} · ${s.service_label}${s.booked_names?.length ? " · " + s.booked_names.join(", ") : ""}`}
                    >
                      {s.time} {role === "admin" && s.booked_names?.length > 0
                        ? `· ${s.booked_names[0]}${s.booked_names.length > 1 ? "+" + (s.booked_names.length-1) : ""}`
                        : `· ${SERVICE_LABEL[s.service_type]}`}
                    </div>
                  ))}
                  {dSlots.length > 4 && (
                    <div className="text-[10px] text-white/40 pl-1">+ {dSlots.length - 4} altri</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
